import { slugify } from "../utils/string"
import { format_price } from "../utils/cart-utils"

const productFormMixin = {
    data: {
        allProductOptions:        [],
        allProductVariants:       [],
        currentProductOptions:    [],
        currentProductVariant:    {},
        isCurrentVariantExist:    true,
        isQuantityExceed:         false,
        quantity:                 1,
    },

    computed: {
        currentVariantName() {
            return this.isCurrentVariantExist ? this.currentProductVariant.optionsValues.join(' - ') : ''
        },

        currentVariantPrice() {
            return this.isCurrentVariantExist ? format_price(this.currentProductVariant.price) : ''
        },

        currentOriginalVariantPrice() {
            return this.isCurrentVariantExist && this.currentProductVariant.compare_at_price ? format_price(this.currentProductVariant.compare_at_price) : ''
        },

        currentVariantQuantity() {
            return this.isCurrentVariantExist ? this.currentProductVariant.quantity : ''
        },

        currentVariantSale() {
            return this.isCurrentVariantExist && this.currentProductVariant.price && this.currentProductVariant.compare_at_price && this.currentProductVariant.price < this.currentProductVariant.compare_at_price  ? 100 - Math.round(this.currentProductVariant.price / this.currentProductVariant.compare_at_price * 100) : 0
        },

        isNotAvailable() {
            return this.isOutOfStock || this.isQuantityNotAvailable
        },

        isOutOfStock() {
            return this.isCurrentVariantExist && !this.currentProductVariant.available
        },

        isQuantityNotAvailable() {
            return this.currentProductVariant.quantity > 0 && this.quantity > this.currentProductVariant.quantity
        },
    },

    watch: {
        currentProductOptions: function (newProductOptions, oldProductOptions) {
            if (this.isQuantityExceed) {
                this.isQuantityExceed = false
            }
            
            this.updateCurrentProductVariant(newProductOptions)
        },

        quantity: function (newQuantity, oldQuantity) {
            if (this.isQuantityExceed && newQuantity < oldQuantity) {
                this.isQuantityExceed = false
            }
        }
    },

    created() {
        // Check module instance
        if (!this.moduleInstance) return console.log("[beforeCreate] - Please store module instance 'this' into 'this.moduleInstance'")

        // Bind data
        const productVariants = this.moduleInstance.productVariants
        const productCurrentVariantId = this.moduleInstance.productCurrentVariantId
        this.allProductOptions = this.moduleInstance.productOptions
        this.allProductQuantities = this.moduleInstance.productQuantities
        this.allProductColors = this.moduleInstance.productColors

        // Set product data
        this.allProductVariants = productVariants
        this.setProductVariantsOptions(productVariants, this.allProductOptions)
        this.setProductVariantsQuantities(productVariants, this.allProductQuantities)
        
        // Set current product variant
        this.currentProductVariant = this.allProductVariants.find(variant => variant.id === productCurrentVariantId)
        
        // Set product options
        this.setProductOptionsIds(this.allProductOptions)
        this.setProductOptionsColors(this.allProductOptions, this.allProductColors)
        this.setCurrentProductOptions(this.currentProductVariant)
    },

    mounted() {
        this.onPopState = this.onPopState.bind(this)
        window.addEventListener('popstate', this.onPopState);
    },
    
    beforeDestroy() {
        window.removeEventListener('popstate', this.onPopState);
    },

    methods: {
        /**
        * Store current product options in an array : [colorName, sizeName, ...]
        */
        setCurrentProductOptions(currentVariant) {
            this.currentProductOptions = currentVariant.options.map(option => option.value)
        },

        /**
        * Override productOptions to add an array which store all variants ids
        */
        setProductOptionsIds(productOptions) {
            let optionIndex = 0

            while (optionIndex < productOptions.length) {
                const option = productOptions[optionIndex]
                const optionName = option.name
                const optionValues = option.values

                option.ids = optionValues.map(optionValue => {
                    return slugify(`${optionName}-${optionValue}`)
                })

                optionIndex++
            }
        },
        
        /**
        * Override productOptions to add an array which store all variants ids
        */
        setProductOptionsColors(productOptions, productColors) {
            let optionIndex = 0

            while (optionIndex < productOptions.length) {
                const option = productOptions[optionIndex]

                if (option.name === "Color") {
                    option.colors = []

                    option.values.forEach(value => {
                        const color = productColors.find(color => color.name === value)
                        const colorValue = color ? color : null
                        option.colors.push(colorValue)
                    })
                }

                optionIndex++
            }
        },

        /**
        * Override productVariants options to add an option object {name, value} and option values array [option1, option2, ...]
        */
        setProductVariantsOptions(variants, options) {
            let variantsIndex = 0

            while (variantsIndex < variants.length) {

                // Add options array 
                variants[variantsIndex].optionsValues =  variants[variantsIndex].options.map((value, index) => {
                    return value
                })

                // Add options object
                variants[variantsIndex].options = variants[variantsIndex].options.map((value, index) => {
                    return { 
                        name: options[index].name, 
                        value: value
                    }
                })

                variantsIndex++
            }
        },

        /**
        * Add quantity number for each variant
        */
        setProductVariantsQuantities(variants, quantities) {
            let variantsIndex = 0

            while (variantsIndex < variants.length) {

                const variantObject = variants[variantsIndex]
                const currentVariant = quantities.find(variant => variant.id === variantObject.id )

                // Add quantity number 
                variantObject.quantity = currentVariant.quantity

                variantsIndex++
            }
        },

        /**
        * Update current product variant and set exist state
        */
        updateCurrentProductVariant(currentOptions) {
            const currentOptionsId = currentOptions.join('')

            this.currentProductVariant = this.allProductVariants.find(productVariant => productVariant.optionsValues.join('') === currentOptionsId )
            
            if (!this.currentProductVariant) {
                this.isCurrentVariantExist = false
            } else {
                this.isCurrentVariantExist = true
                // Update url
                this.updateUrl(this.currentProductVariant.id)
            }
            
            // Hook
            this.onProductVariantChange?.(this.currentProductVariant?.id)
        },

        /**
        * Increment quantity
        */
        increaseQuantity() {
            if (this.quantity > 0) this.quantity++
        },
        /**
        * Decrement quantity
        */
        decreaseQuantity() {
            if (this.quantity > 1) this.quantity-- 
        },

        /**
        * On submit callback
        */
        onSubmit() {
            this.onAddToCart?.(this.currentProductVariant.id, this.quantity)
        },

        /**
        * On popState callback
        */
        onPopState(event) {
            const queryString = window.location.search
            const urlParams = new URLSearchParams(queryString)
            const currentVariantId = urlParams.get('variant')

            if (!currentVariantId) return
            
            // Set current product variant
            this.currentProductVariant = this.allProductVariants.find(variant => variant.id === parseInt(currentVariantId, 10))

            // Set product options
            this.setCurrentProductOptions(this.currentProductVariant)
        },

        /**
        * Update current url with variant parameter
        */
        updateUrl(variantId) {
            if (!variantId) return

            const url = new URL(window.location);
            url.searchParams.set('variant', variantId);
            window.history.replaceState({}, '', url);
        },

        /**
        *  Apply options modifiers
        */
        modifierClass(stringsArr) {
            const string =  stringsArr.join('-')
            return `-${slugify(string)}`
        }
    }
}

export default productFormMixin;
